// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {Icon} from '@wandb/weave/components/Icon';
import {urlPrefixed} from '@wandb/weave/config';
import {capitalizeFirst} from '@wandb/weave/core/util/string';
import React, {useEffect, useMemo, useState} from 'react';

import {agent as agentUrl, runLogs as runLogsUrl} from '../../../../util/urls';
import {RunQueueItem} from '../../types';
import {RunQueueItemModalLogViewer} from '../RunQueueItemModalLogViewer';
import {InfoOption, SelectRunQueueItemInfo} from '../SelectRunQueueItemInfo';

export type RunQueueItemIssuesProps = {
  runQueueItem: RunQueueItem;
};

export const RunQueueItemIssues = ({runQueueItem}: RunQueueItemIssuesProps) => {
  const [expanded, setExpanded] = useState(true);
  const iconName = expanded ? 'chevron-down' : 'chevron-next';

  const {
    entityName,
    projectName,
    displayState,
    launchAgentId: agentID,
    launchAgentName: agentRun,
    associatedRunId: runName,
    error: errorInfo,
    warnings: warningInfos,
  } = runQueueItem;
  const errorAndWarningsSelectOptions = useMemo(() => {
    const errorAndWarnings: InfoOption[] = [];
    if (errorInfo != null) {
      errorAndWarnings.push({
        ...errorInfo,
        type: 'Error',
        value: errorInfo.filePaths[0],
      });
    }
    warningInfos.forEach(warning => {
      if (warning != null) {
        errorAndWarnings.push({
          ...warning,
          type: 'Warning',
          value: warning.filePaths[0],
        });
      }
    });
    return errorAndWarnings;
  }, [errorInfo, warningInfos]);

  const [viewedErrorOrWarning, setViewedErrorOrWarning] =
    useState<InfoOption | null>(
      errorAndWarningsSelectOptions.length > 0
        ? errorAndWarningsSelectOptions[0]
        : null
    );

  useEffect(() => {
    setViewedErrorOrWarning(
      errorAndWarningsSelectOptions.length > 0
        ? errorAndWarningsSelectOptions[0]
        : null
    );
  }, [errorInfo, warningInfos, errorAndWarningsSelectOptions]);
  return (
    <div className="m-16 mt-32">
      <div className="mb-8 flex items-center py-4 text-lg font-semibold">
        <Icon name={iconName} onClick={() => setExpanded(!expanded)} />
        Issues
      </div>
      {expanded &&
        (errorAndWarningsSelectOptions.length === 0 ||
        viewedErrorOrWarning == null ? (
          <div className="px-16">This run has no warnings or failures.</div>
        ) : (
          <div className="px-24">
            <SelectRunQueueItemInfo
              onSelectInfo={setViewedErrorOrWarning}
              options={errorAndWarningsSelectOptions}
              defaultInfoOption={errorAndWarningsSelectOptions[0]}
              selected={viewedErrorOrWarning}
            />
            <div className="my-16 px-4">
              <div className="mb-8  font-semibold">Full error</div>
              {viewedErrorOrWarning.message}
            </div>
            <div className="my-16 px-4">
              <div className="mb-8 font-semibold">Location</div>
              <div className="flex items-center">
                {capitalizeFirst(viewedErrorOrWarning.stage)}
                {viewedErrorOrWarning.stage === 'agent' && agentID != null && (
                  <Button
                    className="ml-8"
                    onClick={() =>
                      window.open(
                        urlPrefixed(agentUrl(entityName, agentID, 'logs'))
                      )
                    }
                    variant="secondary"
                    size="small"
                    icon="job-program-code">
                    View agent logs
                  </Button>
                )}
                {viewedErrorOrWarning.stage === 'run' && runName != null && (
                  <Button
                    onClick={() =>
                      window.open(
                        urlPrefixed(
                          runLogsUrl({entityName, projectName, name: runName})
                        )
                      )
                    }
                    variant="secondary"
                    size="small"
                    icon="job-program-code">
                    View run logs
                  </Button>
                )}
              </div>
            </div>

            {viewedErrorOrWarning.filePaths.length > 0 && (
              <div className="my-16">
                {viewedErrorOrWarning.type === 'Error' ? (
                  <div className="mb-8 font-semibold">Stack Trace</div>
                ) : (
                  <div className="mb-8 font-semibold">Warning Message</div>
                )}
                <RunQueueItemModalLogViewer
                  entityName={entityName}
                  runName={agentRun ?? ''}
                  filePath={viewedErrorOrWarning.filePaths[0]}
                />
              </div>
            )}
          </div>
        ))}
    </div>
  );
};
